// Pure geocoder-helper functions for the place-selection Select ("Explore") flow.
//
// These are intentionally free of any Vue/component or Mapbox dependency so that
// unit and fast-check property tests can import and exercise them directly. They
// mirror the extraction pattern used by placeSelectionDrawHelpers.js.
// Feature: geocoder-populate-country-filter (Task 1.1)

import { getISO3 } from "@/utils/countryMapping";

/**
 * Derive an ISO 3166-1 alpha-3 country code from a geocoder result.
 *
 * Reads only `result.properties.context.country` and never the result type
 * (Req 1.5). It prefers a non-empty `country_code_alpha_3`; when that is
 * empty/absent it maps `country_code` (an alpha-2 code) through `getISO3`.
 *
 * Because `getISO3` echoes its input unchanged on a miss, the mapped value is
 * accepted only when it is three uppercase letters AND differs from the alpha-2
 * input (i.e. an actual mapping occurred); otherwise the result is treated as
 * having no derivable country (Req 1.4).
 *
 * @param {Object} result - The geocoder result feature (GeoJSON).
 * @returns {string|null} An uppercase alpha-3 code, or null when none is derivable.
 */
export function deriveCountryAlpha3(result) {
  const ctx = result && result.properties && result.properties.context;
  const country = ctx && ctx.country ? ctx.country : null;
  if (!country) return null; // Req 1.3

  const a3 = (country.country_code_alpha_3 || "").trim().toUpperCase();
  if (a3) return a3; // Req 1.1

  const a2 = (country.country_code || "").trim().toUpperCase();
  if (!a2) return null; // Req 1.3

  // getISO3 echoes its input on a miss; only accept a real alpha-3 mapping.
  return mapAlpha2ToAlpha3(a2); // Req 1.2, 1.4
}

/**
 * Map an alpha-2 country code to an uppercase alpha-3 code, accepting only a
 * real mapping. Because `getISO3` echoes its input on a miss, the mapped value
 * is accepted only when it is three uppercase letters AND differs from the
 * alpha-2 input. Returns null for empty/unmappable input.
 *
 * @param {*} alpha2 - Candidate alpha-2 code.
 * @returns {string|null} Uppercase alpha-3 code, or null.
 */
function mapAlpha2ToAlpha3(alpha2) {
  const a2 = (alpha2 || "").trim().toUpperCase();
  if (!a2) return null;
  const mapped = getISO3(a2);
  const isAlpha3 =
    typeof mapped === "string" && /^[A-Z]{3}$/.test(mapped) && mapped !== a2;
  return isAlpha3 ? mapped : null;
}

/**
 * Derive an ISO 3166-1 alpha-3 country code from a reverse-geocode result
 * feature. This is separate from `deriveCountryAlpha3` because reverse
 * geocoding by coordinates uses the classic Mapbox Geocoding (v5) response
 * shape, where a country feature carries its alpha-2 code in
 * `properties.short_code` rather than a Search Box `context.country` block.
 *
 * To stay robust across Mapbox response variants, this reads (in order):
 *   1. an explicit alpha-3 field (`properties.country_code_alpha_3`), when a
 *      three-letter code is present;
 *   2. the classic `properties.short_code` alpha-2 (e.g. "us"), mapped via the
 *      alpha-2 -> alpha-3 table;
 *   3. the Search Box `context.country` block (delegated to
 *      `deriveCountryAlpha3`) as a fallback, so a Search Box feature also works.
 *
 * `short_code` for sub-national features can look like "us-ca"; only the part
 * before the hyphen is the country. When `types=country` is requested the
 * top feature is a country, so the whole value is typically the alpha-2 — but
 * splitting on "-" is harmless and defensive.
 *
 * @param {Object} feature - A Mapbox geocoding result feature (GeoJSON).
 * @returns {string|null} An uppercase alpha-3 code, or null when none is derivable.
 */
export function deriveCountryFromReverseGeocode(feature) {
  const props = (feature && feature.properties) || {};

  // 1. Explicit alpha-3, when present.
  const a3 = (props.country_code_alpha_3 || "").trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(a3)) return a3;

  // 2. Classic v5 `short_code` (alpha-2, possibly "us-ca").
  const shortCode = typeof props.short_code === "string" ? props.short_code : "";
  const a2FromShort = shortCode.split("-")[0];
  const fromShort = mapAlpha2ToAlpha3(a2FromShort);
  if (fromShort) return fromShort;

  // 3. Search Box context.country block, if the feature carries one.
  return deriveCountryAlpha3(feature);
}

/**
 * Extract a human-readable `{ city, country }` label pair from a classic Mapbox
 * Geocoding (v5) reverse-geocode response body. Pure and network-free so it can
 * be unit-tested directly.
 *
 * The response's `features` array is ordered most-specific first. This reads:
 *   - `city`: the `text` (or leading token of `place_name`) of the first
 *     feature whose `place_type` includes `place` or `locality`.
 *   - `country`: the `text` (or leading token of `place_name`) of the first
 *     feature whose `place_type` includes `country`.
 *
 * Either value is `null` when no matching feature is found. Defensive on
 * malformed input: a non-object body, missing `features`, or feature entries
 * without usable text all yield `null` for the affected field, never throwing.
 *
 * @param {Object} body - The Mapbox Geocoding v5 response body.
 * @returns {{ city: string|null, country: string|null }}
 */
export function parseReverseGeocodeLabel(body) {
  const features =
    body && Array.isArray(body.features) ? body.features : [];

  const labelOf = (feature) => {
    const text = feature && typeof feature.text === "string" ? feature.text.trim() : "";
    if (text) return text;
    const placeName =
      feature && typeof feature.place_name === "string" ? feature.place_name : "";
    const leading = placeName.split(",")[0].trim();
    return leading || null;
  };

  const hasType = (feature, type) => {
    const types = feature && Array.isArray(feature.place_type) ? feature.place_type : [];
    return types.includes(type);
  };

  let city = null;
  let country = null;

  for (const feature of features) {
    if (city === null && (hasType(feature, "place") || hasType(feature, "locality"))) {
      city = labelOf(feature);
    }
    if (country === null && hasType(feature, "country")) {
      country = labelOf(feature);
    }
  }

  return { city, country };
}
